import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { analyzeSign, type SignedInterval, type Sign } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { INTERSECTION_AREA_DEFAULT_PARAMS, type IntersectionAreaParams } from './intersection-area.model';

/**
 * Creates area visualization between two functions.
 *
 * Features:
 * - Shaded area between two function curves
 * - Vertical boundary lines at xStart and xEnd (dashed, indigo by default)
 * - Optional signed areas: positive regions where f > g (green), negative where f < g (red)
 * - Handles reversed bounds (xStart > xEnd) by swapping and inverting sign colors
 *
 * For signed areas, uses analyzeSign on the difference function (f - g) to find
 * where the curves cross, then creates separate filled regions for each interval.
 */
export class IntersectionAreaCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'intersectionArea';

  /** Maximum number of signed intervals to display */
  private readonly maxIntervals = 20;

  /** Number of sample points for polygon vertices */
  private readonly samplePoints = 100;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, featureTheme } = context;
    const params = def.params as IntersectionAreaParams | undefined;

    if (!params?.of || !Array.isArray(params.of) || params.of.length !== 2) {
      throw new Error('IntersectionArea feature requires params.of to be a tuple of two element IDs');
    }
    if (params.xStart === undefined || params.xEnd === undefined) {
      throw new Error('IntersectionArea feature requires params.xStart and params.xEnd');
    }

    const [fn1, fn2] = this.getEvaluableParents(params.of, context);
    const objects: GeometryElement[] = [];

    // Mutable state for reactive params
    const state = {
      xStart: params.xStart,
      xEnd: params.xEnd,
    };

    // Helper to get ordered bounds and check if reversed
    const getBounds = () => {
      const reversed = state.xStart > state.xEnd;
      const xMin = reversed ? state.xEnd : state.xStart;
      const xMax = reversed ? state.xStart : state.xEnd;
      return { xMin, xMax, reversed };
    };

    // Helper to get effective sign (inverted if bounds are reversed)
    const getEffectiveSign = (sign: Sign, reversed: boolean): Sign => {
      if (!reversed) {
        return sign;
      }
      // Invert sign when bounds are reversed
      if (sign === 'positive') {
        return 'negative';
      }
      if (sign === 'negative') {
        return 'positive';
      }
      return sign;
    };

    const signed = params.signed ?? INTERSECTION_AREA_DEFAULT_PARAMS.signed;

    // Resolve styles
    const polygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, []);
    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display, []);
    const pointStyle = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Resolve signed area styles
    const positivePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['positive']);
    const negativePolygonStyle = resolveFeatureStyle('polygon', this.featureType, context, display, ['negative']);

    // Difference function for sign analysis
    const diffFn = (x: number) => fn1.evaluate(x) - fn2.evaluate(x);

    /**
     * Build polygon vertices for area between two curves over an interval.
     * Goes along fn1 from start to end, then along fn2 from end back to start.
     */
    const buildAreaVertices = (xStart: number, xEnd: number): [number[], number[]] => {
      const xCoords: number[] = [];
      const yCoords: number[] = [];
      const step = (xEnd - xStart) / this.samplePoints;

      // Trace fn1 from start to end
      for (let i = 0; i <= this.samplePoints; i++) {
        const x = xStart + i * step;
        xCoords.push(x);
        yCoords.push(fn1.evaluate(x));
      }

      // Trace fn2 from end back to start
      for (let i = this.samplePoints; i >= 0; i--) {
        const x = xStart + i * step;
        xCoords.push(x);
        yCoords.push(fn2.evaluate(x));
      }

      return [xCoords, yCoords];
    };

    if (signed) {
      // For signed areas, create separate regions for positive and negative intervals
      let cachedIntervals: SignedInterval[] = [];
      let cacheKey = '';
      let cachedReversed = false;

      const getIntervals = (): { intervals: SignedInterval[]; reversed: boolean } => {
        const { xMin, xMax, reversed } = getBounds();
        const fn1Params = 'getParams' in fn1 ? (fn1 as { getParams: () => Record<string, number> }).getParams() : {};
        const fn2Params = 'getParams' in fn2 ? (fn2 as { getParams: () => Record<string, number> }).getParams() : {};
        const paramsKey = [
          ...Object.values(fn1Params).map((v) => v.toFixed(4)),
          ...Object.values(fn2Params).map((v) => v.toFixed(4)),
        ].join(',');
        const newKey = `${xMin.toFixed(2)},${xMax.toFixed(2)}|${paramsKey}`;

        if (newKey !== cacheKey) {
          cacheKey = newKey;
          cachedReversed = reversed;
          cachedIntervals = analyzeSign(diffFn, xMin, xMax, {
            maxResults: this.maxIntervals,
          });
        }
        return { intervals: cachedIntervals, reversed: cachedReversed };
      };

      const getInterval = (index: number): { interval: SignedInterval | null; reversed: boolean } => {
        const { intervals, reversed } = getIntervals();
        return {
          interval: index < intervals.length ? intervals[index] : null,
          reversed,
        };
      };

      // Create placeholder curve-area regions for each potential interval
      for (let i = 0; i < this.maxIntervals; i++) {
        const index = i;

        // Use a curve with dataX/dataY arrays for the filled polygon
        const areaPolygon = board.create(
          'curve',
          [
            [], // dataX - will be set dynamically
            [], // dataY - will be set dynamically
          ],
          {
            fillColor: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 'transparent';
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillColor ? context.themeService.getColor(style.fillColor) : 'transparent';
            },
            fillOpacity: () => {
              const { interval, reversed } = getInterval(index);
              if (!interval) {
                return 0;
              }
              const effectiveSign = getEffectiveSign(interval.sign, reversed);
              const style = effectiveSign === 'positive' ? positivePolygonStyle : negativePolygonStyle;
              return style.fillOpacity ?? 0.3;
            },
            strokeWidth: 0,
            visible: () => {
              const { interval } = getInterval(index);
              return visible && interval !== null;
            },
          },
        ) as GeometryElement;

        // Update the curve data when board updates
        const updateCurveData = () => {
          const { interval } = getInterval(index);
          if (interval) {
            const [xCoords, yCoords] = buildAreaVertices(interval.start, interval.end);
            (areaPolygon as unknown as { dataX: number[]; dataY: number[] }).dataX = xCoords;
            (areaPolygon as unknown as { dataX: number[]; dataY: number[] }).dataY = yCoords;
          } else {
            (areaPolygon as unknown as { dataX: number[]; dataY: number[] }).dataX = [];
            (areaPolygon as unknown as { dataX: number[]; dataY: number[] }).dataY = [];
          }
        };

        // Initial data
        updateCurveData();

        // Register update handler
        board.on('update', updateCurveData);

        objects.push(areaPolygon);
      }
    } else {
      // Single unsigned area between the two curves
      const fillColor = context.themeService.getColor(polygonStyle.fillColor ?? 'indigo');

      // Create a curve with dataX/dataY arrays
      const areaCurve = board.create(
        'curve',
        [
          [], // dataX
          [], // dataY
        ],
        {
          fillColor,
          fillOpacity: polygonStyle.fillOpacity ?? 0.3,
          strokeWidth: 0,
          visible,
        },
      ) as GeometryElement;

      const updateCurveData = () => {
        const { xMin, xMax } = getBounds();
        const [xCoords, yCoords] = buildAreaVertices(xMin, xMax);
        (areaCurve as unknown as { dataX: number[]; dataY: number[] }).dataX = xCoords;
        (areaCurve as unknown as { dataX: number[]; dataY: number[] }).dataY = yCoords;
      };

      // Initial data
      updateCurveData();

      // Register update handler
      board.on('update', updateCurveData);

      objects.push(areaCurve);
    }

    // Create boundary lines at xStart and xEnd
    const strokeColor = context.themeService.getColor(lineStyle.color ?? 'indigo');
    const pointColor = context.themeService.getColor(pointStyle.color ?? 'indigo');
    const dash = lineStyle.dash ?? 3;
    const strokeWidth = lineStyle.strokeWidth ?? 2;
    const pointVisible = pointStyle.visible ?? false;

    // Left boundary line (xStart) - from fn2 to fn1, use reactive state
    const leftLineStart = board.create('point', [() => state.xStart, () => fn2.evaluate(state.xStart)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(leftLineStart);

    const leftLineEnd = board.create('point', [() => state.xStart, () => fn1.evaluate(state.xStart)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(leftLineEnd);

    const leftLine = board.create('line', [leftLineStart, leftLineEnd], {
      straightFirst: false,
      straightLast: false,
      strokeColor,
      strokeWidth,
      dash,
      visible,
    }) as GeometryElement;
    objects.push(leftLine);

    // Right boundary line (xEnd) - from fn2 to fn1, use reactive state
    const rightLineStart = board.create('point', [() => state.xEnd, () => fn2.evaluate(state.xEnd)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(rightLineStart);

    const rightLineEnd = board.create('point', [() => state.xEnd, () => fn1.evaluate(state.xEnd)], {
      visible: pointVisible,
      fixed: true,
      fillColor: pointColor,
      strokeColor: pointColor,
      size: 3,
    }) as GeometryElement;
    objects.push(rightLineEnd);

    const rightLine = board.create('line', [rightLineStart, rightLineEnd], {
      straightFirst: false,
      straightLast: false,
      strokeColor,
      strokeWidth,
      dash,
      visible,
    }) as GeometryElement;
    objects.push(rightLine);

    return {
      id,
      type: 'intersectionArea',
      objects,
      getParams: () => ({ xStart: state.xStart, xEnd: state.xEnd }),
      setParams: (newParams) => {
        if (newParams['xStart'] !== undefined) {
          state.xStart = newParams['xStart'];
        }
        if (newParams['xEnd'] !== undefined) {
          state.xEnd = newParams['xEnd'];
        }
        board.update();
      },
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
